import { useId } from "react";
import { Controller, useForm } from "react-hook-form";

import { trpc } from "@blis/trpc/react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FieldError, Input, Label, TextField } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { PasswordInput } from "@/components/ui/password-input";
import { Switch } from "@/components/ui/switch";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { type OrgRow, readOrgUrl, readTokenSet } from "./org-row";

type OrgDialogForm = {
  url: string;
  /** Write-only: blank keeps existing; non-empty sets a new token. */
  token: string;
  clearToken: boolean;
  enabled: boolean;
};

/**
 * Mirrors `buildSendUrl` in `@blis/services/itransplant/url` for client-side
 * preview, with `{donorId}` left as a placeholder since it isn't known until
 * send time. We can't import the server helper because `@blis/services` isn't
 * a client dependency.
 */
const DONOR_PLACEHOLDER = "__BLIS_DONOR_ID__";

function isHttpOrHttpsUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function buildPreviewUrl(orgUrl: string): string {
  const trimmed = orgUrl.trim();
  if (!trimmed) {
    return "{url}/{donorId}";
  }
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return `${trimmed.replace(/\/+$/, "")}/{donorId}`;
    }
    const basePath = url.pathname.replace(/\/+$/, "");
    url.pathname = `${basePath}/${DONOR_PLACEHOLDER}`;
    return url.toString().replace(DONOR_PLACEHOLDER, "{donorId}");
  } catch {
    return `${trimmed.replace(/\/+$/, "")}/{donorId}`;
  }
}

export function ITransplantOrgConfigDialog({
  org,
  onClose,
}: {
  org: OrgRow | null;
  onClose: () => void;
}) {
  return (
    <Modal isOpen={!!org} onOpenChange={(open) => !open && onClose()}>
      <Dialog>
        {org ? <OrgConfigDialogBody org={org} onClose={onClose} /> : null}
      </Dialog>
    </Modal>
  );
}

function OrgConfigDialogBody({
  org,
  onClose,
}: {
  org: OrgRow;
  onClose: () => void;
}) {
  const formId = useId();
  const savedUrl = readOrgUrl(org.config);
  const tokenAlreadySet = readTokenSet(org.config);

  const form = useForm<OrgDialogForm>({
    defaultValues: {
      url: savedUrl,
      token: "",
      clearToken: false,
      enabled: org.enabled,
    },
  });
  const draftUrl = form.watch("url") ?? "";
  const draftEnabled = form.watch("enabled");
  const draftToken = form.watch("token") ?? "";
  const clearToken = form.watch("clearToken");
  const tokenWillBeSet =
    !clearToken && (draftToken.trim().length > 0 || tokenAlreadySet);

  const urlPreview = buildPreviewUrl(draftUrl);

  const setOrgConfig = trpc.integrations.setOrgConfig.useMutation();
  const setOrgEnabled = trpc.integrations.setOrgEnabled.useMutation();

  async function onSubmit(values: OrgDialogForm) {
    const url = values.url.trim();
    const newToken = values.token.trim();
    const willHaveToken =
      !values.clearToken && (newToken.length > 0 || tokenAlreadySet);

    // Enabling sends requires URL + API token — we can't build a valid
    // request or authenticate without them.
    if (values.enabled && !url) {
      form.setError("url", {
        type: "manual",
        message: "API URL is required to enable sends.",
      });
      return;
    }
    if (values.enabled && url && !isHttpOrHttpsUrl(url)) {
      form.setError("url", {
        type: "manual",
        message: "Enter a valid HTTP or HTTPS URL.",
      });
      return;
    }
    if (values.enabled && !willHaveToken) {
      form.setError("token", {
        type: "manual",
        message: "API token is required to enable sends.",
      });
      return;
    }

    try {
      const enabledChanged = values.enabled !== org.enabled;
      const urlChanged = url !== savedUrl;
      const tokenChanged = values.clearToken || newToken.length > 0;
      const configChanged = urlChanged || tokenChanged;

      // Disable before clearing required send config so we never leave an
      // enabled org without a URL or token if the second request fails.
      // Enable only after config succeeds for the same reason.
      if (enabledChanged && !values.enabled) {
        await setOrgEnabled.mutateAsync({
          kind: "ITRANSPLANT",
          orgId: org.orgId,
          enabled: false,
        });
      }

      if (configChanged) {
        const configPatch: {
          url?: string | null;
          token?: string | null;
        } = {};
        if (urlChanged) {
          configPatch.url = url || null;
        }
        if (values.clearToken) {
          configPatch.token = null;
        } else if (newToken.length > 0) {
          configPatch.token = newToken;
        }
        await setOrgConfig.mutateAsync({
          kind: "ITRANSPLANT",
          orgId: org.orgId,
          config: configPatch,
        });
      }

      if (enabledChanged && values.enabled) {
        await setOrgEnabled.mutateAsync({
          kind: "ITRANSPLANT",
          orgId: org.orgId,
          enabled: true,
        });
      }

      if (enabledChanged) {
        toast.success(
          values.enabled
            ? `Enabled iTransplant sends for ${org.name}`
            : `Disabled iTransplant sends for ${org.name}`,
        );
      } else if (configChanged) {
        toast.success(`Updated iTransplant configuration for ${org.name}`);
      }
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.error("Failed to save", { description: message });
    }
  }

  const isSaving = setOrgConfig.isPending || setOrgEnabled.isPending;

  return (
    <>
      <DialogHeader>
        <DialogTitle>iTransplant configuration for {org.name}</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <form
          id={formId}
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <Controller
            control={form.control}
            name="url"
            rules={{
              validate: (v) => {
                const trimmed = v.trim();
                if (draftEnabled && trimmed.length === 0) {
                  return "API URL is required to enable sends.";
                }
                if (trimmed.length > 0 && !isHttpOrHttpsUrl(trimmed)) {
                  return "Enter a valid HTTP or HTTPS URL.";
                }
                return true;
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                isInvalid={!!error}
                validationBehavior="aria"
              >
                <Label>API URL</Label>
                <Input
                  placeholder="https://host/Interfaces/LabsInc/ACME-42"
                  autoComplete="off"
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <Text size="1" muted className="-mt-2">
            Org endpoint through the org path segment. bLIS appends the donor ID
            on every send. Required only if this org is enabled to send.
          </Text>
          <div className="flex flex-col gap-1">
            <Label>Send URL preview</Label>
            <code
              className="block text-xs font-mono break-all rounded-md border bg-muted/40 px-2 py-1.5 text-muted-foreground"
              aria-live="polite"
            >
              {urlPreview}
            </code>
            <Text size="1" muted>
              <code>{"{donorId}"}</code> is replaced with the accession's donor
              ID at send time.
            </Text>
          </div>

          <Controller
            control={form.control}
            name="token"
            rules={{
              validate: () => {
                if (draftEnabled && !tokenWillBeSet) {
                  return "API token is required to enable sends.";
                }
                return true;
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                value={field.value ?? ""}
                onChange={(v) => {
                  form.setValue("clearToken", false);
                  field.onChange(v);
                }}
                isInvalid={!!error}
                isDisabled={clearToken}
                validationBehavior="aria"
              >
                <div className="flex items-center justify-between gap-2">
                  <Label>API token</Label>
                  {clearToken ? (
                    <Badge color="amber" size="sm">
                      Will clear on save
                    </Badge>
                  ) : tokenAlreadySet ? (
                    <Badge color="green" size="sm">
                      Token set
                    </Badge>
                  ) : (
                    <Badge color="gray" size="sm">
                      Not set
                    </Badge>
                  )}
                </div>
                <PasswordInput
                  autoComplete="off"
                  placeholder={
                    tokenAlreadySet
                      ? "Leave blank to keep existing token"
                      : "Enter API token"
                  }
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          <div className="flex items-center justify-between gap-2 -mt-2">
            <Text size="1" muted>
              Authenticates sends for this org (<code>ITX-AUTHORIZATION</code>).
              Never shown after save.
            </Text>
            {tokenAlreadySet || clearToken ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                isDisabled={isSaving}
                onPress={() => {
                  if (clearToken) {
                    form.setValue("clearToken", false);
                    return;
                  }
                  // Clearing while enabled is blocked — disable first. Keep
                  // the button pressable so this message can surface.
                  if (draftEnabled) {
                    form.setError("token", {
                      type: "manual",
                      message:
                        "Disable sends before clearing the API token, or replace it with a new value.",
                    });
                    return;
                  }
                  form.setValue("token", "");
                  form.setValue("clearToken", true);
                  form.clearErrors("token");
                }}
              >
                {clearToken ? "Undo clear" : "Clear token"}
              </Button>
            ) : null}
          </div>

          <Controller
            control={form.control}
            name="enabled"
            render={({ field }) => (
              <div className="flex items-center justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2">
                <div className="flex flex-col">
                  <Label>Enable sends from this org</Label>
                  <Text size="1" muted>
                    When on, finalized reports for this org's accessions are
                    forwarded to iTransplant.
                  </Text>
                </div>
                <Switch
                  aria-label="Enable iTransplant sends from this org"
                  isSelected={field.value}
                  onChange={field.onChange}
                />
              </div>
            )}
          />
        </form>
      </DialogBody>
      <DialogFooter>
        <Button
          type="submit"
          form={formId}
          isPending={isSaving}
          isDisabled={isSaving}
        >
          Save
        </Button>
        <DialogCloseButton isDisabled={isSaving}>Cancel</DialogCloseButton>
      </DialogFooter>
    </>
  );
}
